<i18n>
{
  "fr": {
    "station-title": "Station: ",
    "available-data": "Données disponibles",
    "extract-data": "Extraire des données",
    "aggregation-mode": "Aggrégation temporelle",
    "raw": "Données brutes",
    "hh": "Horaire",
    "dd": "Journalière",
    "mm": "Mensuelle",
    "yyyy": "Anuelle",
    "maxValue": "Maximum",
    "avgValue": "Moyenne",
    "minValue": "Minimum",
    "available-values": "Type d'aggrégation",
    "timezone-is": "Les heures sont en",
    "hydrological-year": "Année hydrologique",
    "publicId": "ID cantonal",
    "manage-obs-point": "Gérer le point d'observation",
    "close-tab": "Fermer l'onglet"
  },
  "de": {

  }
}
</i18n>

<template>
  <div class="container" id="observation-point-data">
    <md-button class="md-icon-button md-dense md-raised margin" @click="closeTab()">
      <md-icon>close</md-icon>
      <md-tooltip>{{ $t('close-tab') }}</md-tooltip>
    </md-button>
    <div class="main-timeseries">
      <Timeframe
        @mouseover="(timestamp) => highlightedTimestamp = timestamp"
        ref="timeframe"
      />
      <div class="option-bar">
        <md-field class="option">
          <label for="aggregationMode">{{ $t('hydrological-year') }}</label>
          <md-select v-model="selectedHydrologicalYear" @md-closed="changeHydrologicalYear()">
            <md-option value="-">-</md-option>
            <md-option v-for="year in availableHydrologicalYears" :value="year" :key="year">{{ year }}</md-option>
          </md-select>
        </md-field>
        <md-field class="option">
          <label for="aggregationMode">{{ $t('aggregation-mode') }}</label>
          <md-select v-model="currentAggregationMode">
            <md-option v-for="value in availableAggregationMode" :value="value" :key="value">{{ $t(value) }}</md-option>
          </md-select>
        </md-field>
        <md-field class="option" v-if="availableValues.length > 1">
          <label>{{ $t('available-values') }}</label>
          <md-select v-model="selectedValue">
            <md-option v-for="availableValue in availableValues" :value="availableValue" :key="availableValue">{{ $t(availableValue) }}</md-option>
          </md-select>
        </md-field>
        <div class="utc-offset">{{ $t('timezone-is') }} <b>{{ utcOffsetDisplay }}</b></div>
      </div>
      <TimeSeries
        :observationPointId="observationPointId"
        :highlightedTimestamp="highlightedTimestamp"
        :selectedValue="selectedValue"
      />
  </div>
    <div class="right-panel">
      <div class="obs-point-description tool" v-if="!loading">
        <h3>{{$t('station-title')}} {{ observationPoint.name }}</h3>
        <div class="info" v-if="!loading">
          <h4>{{ $t('publicId') }}</h4>
          <span class="info-value">{{ observationPoint.publicId }}</span>
        </div>
        <md-button class="manage-obs-point md-raised" v-if="isOperator" @click="gotoObsPointAdmin">{{ $t('manage-obs-point') }}</md-button>
      </div>
      <div class="data tool" v-if="!loading">
        <h3>{{ $t('available-data') }}</h3>
        <div class="tool-content">
          <md-button class="md-raised" @click="showExportModal = true" v-if="canExport">{{ $t('extract-data') }}</md-button>
        </div>
      </div>
    </div>
    <ExportDataModal :show="showExportModal" :observationPoint="observationPoint" @close="showExportModal = false" />
  </div>
</template>

<script>

import moment from 'moment'

import Timeframe from '@/components/tool/timeframe'
import TimeStoreMixin from '@/mixins/store/time'
import TimeSeries from '@/components/timeseries/observationPoint'
import ExportDataModal from '@/components/export/modal'
import AccessToken from '@/utils/accessToken'

import ObservationPointProxy from '@/proxies/observationPoint'

export default {
  name: 'ObservationPointData',
  components: {
    Timeframe,
    TimeSeries,
    ExportDataModal
  },
  mixins: [
    TimeStoreMixin
  ],
  watch: {
    aggregationMode () {
      if (this.debounceAggregationMode) {
        this.debounceAggregationMode = false
        return
      }
      this.currentAggregationMode = this.aggregationMode
      this.debounceAggregationMode = true
    },
    currentAggregationMode () {
      if (this.debounceAggregationMode) {
        this.debounceAggregationMode = false
        return
      }
      this.setAggregationMode(this.currentAggregationMode)
      this.debounceAggregationMode = true
    },
    dateTo () {
      if (this.frameSize.asYears() > 1 || this.selectedHydrologicalYear === '-') this.selectedHydrologicalYear = '-'
      else this.selectedHydrologicalYear = this.currentHydrologicalYear
    },
  },
  computed: {
    observationPointId () {
      return parseInt(this.$route.params.id)
    },
    availableAggregationMode () {
      switch (this.aggregationModeFromFrameSize) {
        case 'yy': return ['mm', 'yyyy']
        case 'mm': return ['dd', 'mm', 'yyyy']
        case 'dd':
          if (this.frameSize !== null && this.frameSize.asYears() <= 1) return ['raw', 'dd', 'mm']
          return  ['dd', 'mm']
        case 'raw':
        default:
          return ['raw', 'dd']
      }
    },
    availableValues () {
      if (this.currentAggregationMode === 'raw') return ['value']
      else return ['minValue', 'avgValue', 'maxValue']
    },
    canExport () {
      return AccessToken.get().hasRight('strates_export')
    },
    isOperator () {
      return AccessToken.get().hasRight('strates_operator')
    }
  },
  mounted() {
    this.cleanEvents()
    ObservationPointProxy.get(this.observationPointId).then((observationPoint) => {
      this.observationPoint = observationPoint
      this.selectedHydrologicalYear = '-'
      this.loading = false
      setTimeout(() => {
        this.showLastValues()
      }, 500)
    }).catch(this.handleError)
  },
  data () {
    return {
      highlightedTimestamp: null,
      observationPoint: null,
      loading: true,
      showExportModal: false,
      currentAggregationMode: 'dd',
      debounceAggregationMode: false,
      selectedValue: this.currentAggregationMode === 'raw' ? 'value' : 'avgValue',
      selectedHydrologicalYear: '-'
    }
  },
  methods: {
    handleError (e) {
      console.error(e)
    },
    changeHydrologicalYear () {
      if (this.selectedHydrologicalYear === '-') return this.showLastValues()
      this.setTimeframe(this.getHydrologicalYears(this.selectedHydrologicalYear))
      if (typeof this.$refs.timeframe === 'undefined') return
      this.$refs.timeframe.moveTo([this.dateFrom, this.dateTo])
    },
    showLastValues () {
      let dateTo = this.lastValueDate
      let dateFrom = moment.utc(this.lastValueDate).subtract(1, 'year')
      this.setTimeframe([dateFrom, dateTo])
      if (typeof this.$refs.timeframe === 'undefined') return
      this.$refs.timeframe.moveTo([this.dateFrom, this.dateTo])
    },
    closeTab () {
      window.close()
    },
    gotoObsPointAdmin () {
      this.$router.push({ name: 'admin-observation-point-data', params: { id: this.observationPointId } })
    }
  }
}
</script>

<style>
  .md-datepicker-dialog {
    z-index: 20;
  }
</style>

<style lang="scss" scoped>
  .container {
    display: flex;
    flex-direction: row;

    .margin {
      margin-top: 15px;
      margin-left: 10px;
    }

    .main-timeseries {
      width: 80vw;
    }

    .right-panel {
      width: 20vw;
      height: 100vh;
      background: #fafafa;
      border-left: 1px solid #d4d4d4;
      z-index: 10;
      padding: 15px;

      .tool {
        background-color: white;
        padding: 10px;
        margin-top: 15px;
        border: 1px solid #d4d4d4;

        h3 {
          margin-top: 5px;
          margin-bottom: 5px;
        }

        h4 {
          margin: 0px;
          margin-bottom: 5px;
          font-size: 13px;
        }

        &.obs-point-description {
          .info {
            display: flex;
            align-items: center;
            margin-top: 7px;

            h4 {
              width: 160px;
            }

            .info-value {
              margin-left: 5px;
            }
          }
        }

        &.data {
          .tool-content {
            display: flex;
            justify-content: center;
          }
        }
      }
    }
  }
  .option-bar {
    display: flex;
    justify-content: flex-end;
    align-items: baseline;
    padding-right: 75px;

    .option {
      max-width: 140px;
      margin-right: 25px;

      &.md-field {
        margin-bottom: 0px !important;
      }
    }

    .utc-offset {
      font-style: italic;
      color: rgba(0,0,0,0.54);
      font-size: 13px;
      margin-right: 25px;
    }
  }

  .manage-obs-point {
    margin-left: auto;
    margin-right: auto;
    display: block;
    margin-top: 15px;
  }
</style>

<style lang="scss">
  #observation-point-data {
    .timeframe-tool {
      padding-right: 85px;
    }

    .container.graphs {
      margin-top: 12.5px;
      padding-top: 12.5px;
      border: 1px solid #d4d4d4;
      border-right: none;
    }
  }
</style>
